import { useState } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { css } from '@emotion/react';

import { ILazyDropdownOption } from 'components/lazyDropdown/LazyDropdown';
import { DataSourceDropdown } from 'features/layer/components/DataSourceDropdown';
import { GroupLayerTreeView } from 'features/layer/components/GroupLayerTreeView';
import { IGroupLayer } from 'features/layer/models/interfaces/IGroupLayer';
import { SridDropdown } from 'features/spatial/components/SridDropdown';
import { PermissionType } from 'features/seismic/models/enums/PermissionType';
import { IQueueParamProps } from '../../../models/interfaces/IQueueParamProps';

const containerStyle = css({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxSizing: 'border-box',
    padding: 24,
    gap: 16,
});

type IQueueUploadShapefileParamProps = IQueueParamProps;

export function QueueUploadShapefileParam({ onParamsChange, initialValues }: IQueueUploadShapefileParamProps) {
    const iv = initialValues ?? {};
    const initialSrid = iv.Srid ? { value: Number(iv.Srid), label: iv.Srid } as ILazyDropdownOption<number> : null;

    const [srid, setSrid] = useState<ILazyDropdownOption<number> | null>(initialSrid);
    const [dataSource, setDataSource] = useState<number | null>(iv.DataSourceId ? Number(iv.DataSourceId) : null);
    const [groupLayer, setGroupLayer] = useState<IGroupLayer | null>(iv.GroupId ? { Id: Number(iv.GroupId) } as IGroupLayer : null);
    const [permission, setPermission] = useState<PermissionType>(iv.Permission ? Number(iv.Permission) as PermissionType : PermissionType.Private);

    function notify(
        nextSrid: ILazyDropdownOption<number> | null,
        nextDataSource: number | null,
        nextGroupLayer: IGroupLayer | null,
        nextPermission: PermissionType,
    ) {
        onParamsChange?.({
            Srid: String(nextSrid?.value ?? ''),
            DataSourceId: String(nextDataSource ?? ''),
            GroupId: String(nextGroupLayer?.Id ?? ''),
            Permission: String(nextPermission),
        });
    }

    function handleSridChange(value: ILazyDropdownOption<number> | null) {
        setSrid(value);
        notify(value, dataSource, groupLayer, permission);
    }

    function handleDataSourceChange(value: number | null) {
        setDataSource(value);
        setGroupLayer(null);
        notify(srid, value, null, permission);
    }

    function handleGroupLayerChange(value: IGroupLayer | null) {
        setGroupLayer(value);
        notify(srid, dataSource, value, permission);
    }

    function handlePermissionChange(event: SelectChangeEvent<PermissionType>) {
        const value = event.target.value as PermissionType;
        setPermission(value);
        notify(srid, dataSource, groupLayer, value);
    }

    return (
        <Box css={containerStyle}>
            <SridDropdown value={srid} onChange={handleSridChange} />
            <FormControl fullWidth>
                <InputLabel>Permission</InputLabel>
                <Select<PermissionType>
                    value={permission}
                    label='Permission'
                    onChange={handlePermissionChange}
                    MenuProps={{ style: { zIndex: 1000000 } }}
                >
                    <MenuItem value={PermissionType.Private}>Private</MenuItem>
                    <MenuItem value={PermissionType.Public}>Public</MenuItem>
                    <MenuItem value={PermissionType.InCompany}>In Company</MenuItem>
                </Select>
            </FormControl>
            <DataSourceDropdown value={dataSource} onChange={handleDataSourceChange} />
            <GroupLayerTreeView dataSourceId={dataSource} value={groupLayer} onChange={handleGroupLayerChange} />
        </Box>
    );
}
